import { useEffect, useRef, useState, useMemo } from 'react';
import { searchGearExchange, type GXMultiSearchResponse } from '@/api/gearExchange';
import type { FordShareEntry } from '@/api/campaigns';

/**
 * Hook that fetches Gear Exchange assets for a campaign's FordShare IDs.
 *
 * Handles the race condition where the optimistic UI update runs before the
 * backend PATCH commits, by retrying up to 5 times (1.5s apart) until the
 * backend response includes the expected FordShare IDs.
 */
export function useGearExchangeSearch(
  campaignId: string | undefined,
  fordshareEntries: FordShareEntry[] | null | undefined,
) {
  const [gxData, setGxData] = useState<GXMultiSearchResponse | null>(null);
  const [gxLoading, setGxLoading] = useState(false);
  const [gxError, setGxError] = useState(false);

  const fordshareIds = useMemo(
    () => fordshareEntries?.map((e) => e.id) ?? null,
    [fordshareEntries],
  );
  const fordshareIdsKey = useMemo(
    () => fordshareIds?.slice().sort().join(',') ?? '',
    [fordshareIds],
  );

  const gxFordshareKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // Reset state when prerequisites are missing (no campaign or all FordShare
    // IDs removed) to avoid showing stale results.
    if (!campaignId || !fordshareIdsKey) {
      setGxData(null);
      setGxError(false);
      setGxLoading(false);
      gxFordshareKeyRef.current = null;
      return;
    }

    // Skip if we already have results for this exact key
    const cacheKey = `${campaignId}:${fordshareIdsKey}`;
    if (cacheKey === gxFordshareKeyRef.current) return;

    let cancelled = false;
    const expectedIds = fordshareIdsKey.split(',').sort().join(',');

    async function fetchGx() {
      try {
        setGxLoading(true);
        setGxError(false);
        let result = await searchGearExchange(campaignId!);

        // Retry if the backend hasn't picked up the new fordshare entries yet.
        // This happens because the optimistic UI update races with the PATCH commit.
        // We compare the response's fordshare_ids against the expected set.
        let retries = 0;
        const idsMatch = (res: GXMultiSearchResponse) =>
          res.fordshare_ids.slice().sort().join(',') === expectedIds;

        while (!idsMatch(result) && retries < 5 && !cancelled) {
          await new Promise((r) => setTimeout(r, 1500));
          if (cancelled) return;
          result = await searchGearExchange(campaignId!);
          retries++;
        }

        if (!cancelled) {
          if (idsMatch(result)) {
            setGxData(result);
            gxFordshareKeyRef.current = cacheKey;
          } else {
            // Backend still doesn't reflect the expected IDs after all retries.
            // Don't show mismatched results — leave data as null so the UI
            // doesn't display stale/wrong assets. The next cache invalidation
            // or page refresh will retry.
            setGxData(null);
          }
        }
      } catch {
        if (!cancelled) setGxError(true);
      } finally {
        if (!cancelled) setGxLoading(false);
      }
    }

    fetchGx();
    return () => {
      cancelled = true;
    };
  }, [campaignId, fordshareIdsKey]);

  return { gxData, gxLoading, gxError };
}
